import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    model,
    output,
    signal,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {catchError, of} from 'rxjs';
import {TranslatePipe, includesIgnoreCase, keyComparator} from '@brunata-angular/util';
import {LoadComponent} from '@brunata-angular/ui';
import {BuildingService} from '@shared/services/building.service';

type SystemAccess = {
    buildingNo: number;
    address: string;
};

/**
 * Two-column building-access picker (available ↔ assigned) shared by create-user and
 * user-detail. Owns the building fetch, search, and selection UI; the assigned building
 * numbers are a two-way `model`, and `changed` fires on user interaction (so a parent form
 * can mark itself dirty) but not when the parent seeds the value.
 */
@Component({
    selector: 'app-system-access-picker',
    standalone: true,
    imports: [TranslatePipe, LoadComponent],
    templateUrl: './system-access-picker.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemAccessPickerComponent {
    private readonly buildingService = inject(BuildingService);

    readonly assignedBuildingNos = model<number[]>([]);
    readonly changed = output<void>();

    private readonly buildings = toSignal(
        this.buildingService.getBuildings().pipe(catchError(() => of([]))),
        {initialValue: null},
    );
    protected readonly buildingsLoading = computed(() => this.buildings() === null);

    protected readonly availableSearch = signal('');
    protected readonly assignedSearch = signal('');

    private readonly assignedNos = computed(() => new Set(this.assignedBuildingNos()));

    private readonly allSystems = computed<SystemAccess[]>(() =>
        (this.buildings() ?? [])
            .map(building => ({
                buildingNo: Number(building.building.buildingNo),
                address: this.buildingLabel(
                    building.building.buildingName,
                    building.building.address1,
                ),
            }))
            // Sort by building number so both columns present in a stable, predictable order.
            .sort(keyComparator(system => system.buildingNo)),
    );

    protected readonly availableSystems = computed(() =>
        this.filterSystems(
            this.allSystems().filter(system => !this.assignedNos().has(system.buildingNo)),
            this.availableSearch(),
        ),
    );

    protected readonly assignedSystems = computed(() =>
        this.filterSystems(
            this.allSystems().filter(system => this.assignedNos().has(system.buildingNo)),
            this.assignedSearch(),
        ),
    );

    assignSystem(system: SystemAccess): void {
        this.mutateAssigned(nos => nos.add(system.buildingNo));
    }

    removeSystem(system: SystemAccess): void {
        this.mutateAssigned(nos => nos.delete(system.buildingNo));
    }

    assignAll(systems: SystemAccess[]): void {
        this.mutateAssigned(nos => systems.forEach(system => nos.add(system.buildingNo)));
    }

    removeAll(systems: SystemAccess[]): void {
        this.mutateAssigned(nos => systems.forEach(system => nos.delete(system.buildingNo)));
    }

    onAvailableSearchChange(event: Event): void {
        const input = event.target;
        if (input instanceof HTMLInputElement) {
            this.availableSearch.set(input.value);
        }
    }

    onAssignedSearchChange(event: Event): void {
        const input = event.target;
        if (input instanceof HTMLInputElement) {
            this.assignedSearch.set(input.value);
        }
    }

    private mutateAssigned(mutate: (nos: Set<number>) => void): void {
        const next = new Set(this.assignedNos());
        mutate(next);
        this.assignedBuildingNos.set([...next]);
        this.changed.emit();
    }

    // Show the building name, falling back to the street address when the name is
    // missing or blank (PO request).
    private buildingLabel(name: string | null, address: string | null): string {
        if (name != null && name !== '') {
            return name;
        }
        return address ?? '';
    }

    private filterSystems(systems: SystemAccess[], search: string): SystemAccess[] {
        return systems.filter(
            system =>
                includesIgnoreCase(system.buildingNo.toString(), search) ||
                includesIgnoreCase(system.address, search),
        );
    }
}
